import type { CSSProperties } from 'react';
import { arcTint } from '../design/tokens';
import type { Container, Theme } from '../design/tokens';
import { listOf } from '../entity/entity';
import type { Arc } from '../plan/arc';
import { tagTint } from '../plan/registries';
import type { Registries } from '../plan/registries';

/**
 * A card's fill, from the arc's first tag.
 *
 * Worth knowing what this signal is actually worth here: 89 of 91 arcs carry
 * the same tag, so a tinted wall is very nearly one hue. It reads as a
 * surface, not as a legend — which is why the layouts group by AREA, the key
 * that is genuinely spread, rather than expecting colour to do it.
 */
export function tintOf(arc: Arc, registries: Registries, theme: Theme): Container | null {
  const first = listOf(arc.frontMatter['tags'])[0];
  const hue = first ? tagTint(registries, first) : null;
  return hue ? arcTint(theme, hue) : null;
}

/** What holds an item together. Only `tint` is reachable now — the switch that
 *  offered `none` is gone, and the rendering stays because a row in the next
 *  band is still drawn without a card around it. */
export type Hold = 'tint' | 'none';

/** The card fill as a style, with the untinted card as the fallback — every
 *  holder that paints a row asks for it the same way. */
export function tintStyle(arc: Arc, registries: Registries, theme: Theme): CSSProperties {
  const tint = tintOf(arc, registries, theme);
  if (!tint) return { background: 'var(--bgCard)', borderColor: 'var(--hair)' };
  return { background: tint.fill, borderColor: tint.border };
}
